import { useMemo } from "react";

import { getErrorMessage } from "@/core/services";
import { useAsyncQuery } from "@/ds";
import {
  getObraEmbarcacaoInformacoes,
  listObraCentrosCusto,
  listObraEmbarcacoes,
  listObraLimitesClasse,
  listObraLocais,
  listObraPeriodos,
  listObraStatus,
  listObraTiposObra,
} from "./obras.gateway";
import type {
  CentroCustoOpcao,
  ObraEmbarcacaoInformacoes,
  ObraEmbarcacaoOpcao,
  ObraLimiteClasseDetalhe,
  ObraLocalOpcao,
  ObraPeriodoOpcao,
  ObraQueryState,
  ObraStatusOpcao,
  TipoObraOpcao,
} from "./obras.types";

export const OBRAS_FORBIDDEN_MESSAGE = "Você não tem permissão para consultar os dados da obra.";
export const OBRAS_ERROR_MESSAGE = "Não foi possível carregar os dados.";

/** Prefixo das chaves destas consultas: a Nova Obra abre o cache por ele (`manterConsultasEmCache`). */
export const OBRA_CONSULTAS_PREFIXO = "obra:";
/** Prefixo das consultas que dependem da embarcação escolhida. */
export const OBRA_CONSULTAS_DA_EMBARCACAO_PREFIXO = `${OBRA_CONSULTAS_PREFIXO}embarcacao:`;

const chaveDaEmbarcacao = (embarcacaoId: string | null, consulta: string) =>
  embarcacaoId === null
    ? null
    : `${OBRA_CONSULTAS_DA_EMBARCACAO_PREFIXO}${embarcacaoId}:${consulta}`;

function useObraQuery<T>(
  load: (signal: AbortSignal) => Promise<T>,
  initial: T,
  key: string | null,
): ObraQueryState<T> {
  const { status, data, errorMessage } = useAsyncQuery({
    fetcher: load,
    queryKey: key ?? "",
    initialData: initial,
    enabled: key !== null,
    forbiddenMessage: OBRAS_FORBIDDEN_MESSAGE,
    errorMessage: OBRAS_ERROR_MESSAGE,
    resolveErrorMessage: getErrorMessage,
  });

  return { status, data, errorMessage };
}

const NO_EMBARCACOES: ObraEmbarcacaoOpcao[] = [];
const NO_LOCAIS: ObraLocalOpcao[] = [];
const NO_STATUS: ObraStatusOpcao[] = [];
const NO_PERIODOS: ObraPeriodoOpcao[] = [];
const NO_TIPOS_OBRA: TipoObraOpcao[] = [];
const NO_LIMITES_CLASSE: ObraLimiteClasseDetalhe[] = [];
const NO_CENTROS_CUSTO: CentroCustoOpcao[] = [];

export function useObraEmbarcacoes() {
  return useObraQuery(
    (signal) => listObraEmbarcacoes({ signal }),
    NO_EMBARCACOES,
    `${OBRA_CONSULTAS_PREFIXO}embarcacoes`,
  );
}

export function useObraLocais() {
  return useObraQuery(
    (signal) => listObraLocais({ signal }),
    NO_LOCAIS,
    `${OBRA_CONSULTAS_PREFIXO}locais`,
  );
}

export function useObraPeriodos() {
  return useObraQuery(
    (signal) => listObraPeriodos({ signal }),
    NO_PERIODOS,
    `${OBRA_CONSULTAS_PREFIXO}periodos-obra`,
  );
}

/** Só os status oferecidos na criação (RN05); a lista da API traz a tabela inteira. */
export function useObraStatusCriacao() {
  const state = useObraQuery(
    (signal) => listObraStatus({ signal }),
    NO_STATUS,
    `${OBRA_CONSULTAS_PREFIXO}status`,
  );
  return { ...state, data: state.data.filter((status) => status.disponivelNaCriacao) };
}

/** Todos os status da tabela (filtro e colunas dos cards da listagem, HU 2521). */
export function useObraStatusTodos() {
  return useObraQuery(
    (signal) => listObraStatus({ signal }),
    NO_STATUS,
    `${OBRA_CONSULTAS_PREFIXO}status`,
  );
}

export function useObraEmbarcacaoInformacoes(embarcacaoId: string | null) {
  return useObraQuery<ObraEmbarcacaoInformacoes | null>(
    (signal) => getObraEmbarcacaoInformacoes(embarcacaoId ?? "", { signal }),
    null,
    chaveDaEmbarcacao(embarcacaoId, "informacoes"),
  );
}

export function useObraTiposObra() {
  return useObraQuery(
    (signal) => listObraTiposObra({ signal }),
    NO_TIPOS_OBRA,
    `${OBRA_CONSULTAS_PREFIXO}tipos-obra`,
  );
}

/** Só os CCs não bloqueados da embarcação (RN20); lista vazia = embarcação sem CC (Impasse 33). */
export function useObraCentrosCusto(embarcacaoId: string | null) {
  const state = useObraQuery(
    (signal) => listObraCentrosCusto(embarcacaoId ?? "", { signal }),
    NO_CENTROS_CUSTO,
    chaveDaEmbarcacao(embarcacaoId, "centros-custo"),
  );
  const data = useMemo(() => state.data.filter((centro) => !centro.bloqueado), [state.data]);
  return { ...state, data };
}

/** Limites de classe da embarcação (HU 2549); sem embarcação a consulta não roda. */
export function useObraLimitesClasse(embarcacaoId: string | null) {
  return useObraQuery(
    (signal) => listObraLimitesClasse(embarcacaoId ?? "", { signal }),
    NO_LIMITES_CLASSE,
    chaveDaEmbarcacao(embarcacaoId, "limites-classe"),
  );
}
